// src/pages/MypageExtra.tsx
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';

import MypageLayout from '../components/layouts/MypageLayout';
import { ProfileStatus, ProfileTraffic } from '../components/MypageNavbar';

import { getUserProfile } from '../api/userProfile';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

type Tab = 'request' | 'history';

interface UserProfile {
  full_name?: string;
  furigana?: string;
  gender?: string;
  birth_date?: string;
  phone_number?: string;
  postal_code?: string;
  address?: string;
}

const digits = (s?: string | null) => String(s || '').replace(/\D/g, '');
const isYYYYMMDD = (s?: string | null) => /^\d{4}-\d{2}-\d{2}$/.test(String(s || ''));

const MypageExtra: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { logout } = useAuth();

  const [activeTab, setActiveTab] = useState<Tab>('request');

  // ✅ 仅用于 Navbar 圆点展示（不弹提醒）
  const [profileStatus, setProfileStatus] = useState<ProfileStatus>({ state: 'red', missing: [] });

  // ✅ 修改密码弹窗
  const [showPwdDlg, setShowPwdDlg] = useState(false);

  // ✅ 优惠券弹窗（Footer 的“クーポンを見る”用）
  const [showCouponDlg, setShowCouponDlg] = useState(false);

  // 与 Records/Regular 一致：furigana 不算必填，只计算圆点
  const computeProfileStatus = (p: UserProfile | null) => {
    const missing: string[] = [];

    const fullName = String(p?.full_name || '').trim();
    const postal = digits(p?.postal_code);
    const addr = String(p?.address || '').trim();

    const gender = String(p?.gender || '').trim();
    const birth = String(p?.birth_date || '').trim();
    const phone = digits(p?.phone_number);

    const nameOK = fullName.length > 0;
    const postalOK = /^\d{7}$/.test(postal);
    const addrOK = addr.length > 0;
    if (!nameOK) missing.push(t('profile.full_name') || '氏名');
    if (!postalOK) missing.push(t('profile.postal_code') || '郵便番号（7桁）');
    if (!addrOK) missing.push(t('profile.address') || '住所');

    const genderOK = gender.length > 0;
    const birthOK = isYYYYMMDD(birth);
    const phoneOK = /^\d{10,13}$/.test(phone);
    if (!genderOK) missing.push(t('profile.gender') || '性別');
    if (!birthOK) missing.push(t('profile.birth_date') || '生年月日');
    if (!phoneOK) missing.push(t('profile.phone_number') || '電話番号');

    // 電話番号も「重要」に格上げ（無断キャンセル時などに患者へ連絡できるようにするため）
    const hasCriticalMissing = !nameOK || !postalOK || !addrOK || !phoneOK;
    const state: ProfileTraffic = missing.length === 0 ? 'green' : hasCriticalMissing ? 'red' : 'yellow';

    setProfileStatus({ state, missing });
  };

  useEffect(() => {
    (async () => {
      try {
        const p = await getUserProfile();
        computeProfileStatus(p || null);
      } catch {
        computeProfileStatus(null);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <MypageLayout
      title={t('navbar.mypage')}
      profileStatus={profileStatus}
    >
      <div className="flex flex-col flex-1">
        <div className="flex gap-6 border-b mb-4">
          <button
            onClick={() => setActiveTab('request')}
            className={`py-2 text-sm font-semibold border-b-2 transition ${activeTab === 'request'
                ? 'text-coral border-coral'
                : 'text-gray-400 border-transparent hover:text-gray-600'
              }`}
          >
            {t('mypage.extra_request', '定期配送リクエスト')}
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`py-2 text-sm font-semibold border-b-2 transition ${activeTab === 'history'
                ? 'text-coral border-coral'
                : 'text-gray-400 border-transparent hover:text-gray-600'
              }`}
          >
            {t('mypage.purchase_history')}
          </button>
        </div>

        <div className="space-y-6 flex-1 flex flex-col items-center justify-center p-8">
          <h2 className="text-xl font-bold text-gray-800">
            {t('mypage.extra_delivery')}
          </h2>

          <p className="text-sm text-gray-700 rounded px-4 py-2 bg-gray-100 inline-block w-fit">
            {t('mypage.extra_info')}
          </p>

          <p className="text-gray-600 pt-6">
            {activeTab === 'request'
              ? t('mypage.no_extra_delivery')
              : t('mypage.no_purchase_history', '購入履歴はまだありません')}
          </p>
        </div>
      </div>
    </MypageLayout>

  );
};

export default MypageExtra;
